import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ContractsService, formatB2bDevolucaoReturnDateUtc } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { VehiclesService } from '../../../services/vehicles.service';
import {
  B2B_DESVINCULAR_CONDUTOR_MOTIVO_OPTIONS,
  B2B_DESVINCULAR_OUTRO_MIN_LENGTH,
  B2bDesvincularCondutorMotivo,
  resolveDesvincularMotivoPayload
} from '../../../models/b2b-desvincular-condutor-motivo';

@Component({
  selector: 'app-b2b-contracts-item-view',
  templateUrl: './b2b-contracts-item-view.component.html',
  styleUrls: ['./b2b-contracts-item-view.component.css']
})
export class B2bContractsItemViewComponent implements OnInit {
  item: any = null;
  isLoading = false;
  contractId: number | null = null;
  itemId: number | null = null;
  contractNumber = '';
  contractName = '';

  /** Linha selecionada apenas para troca/substituição de veículo (PUT substituir — não é devolução). */
  selectedVehicleRow: any = null;

  showSubstituirModal = false;
  showDevolverModal = false;
  isSavingSubstituir = false;
  isSavingDevolver = false;
  isSearchingVehicle = false;
  devolverVehicleSnapshotId: number | null = null;

  /** Desvincular condutor neste item — PUT .../items/{itemId}/vehicle-snapshots/{id}/desvincular */
  showDesvincularModal = false;
  showDesvincularOutroModal = false;
  vehicleRowForDesvincular: any = null;
  desvincularForm: FormGroup;
  desvincularOutroForm: FormGroup;
  isSavingDesvincular = false;
  readonly desvincularMotivoOptions = B2B_DESVINCULAR_CONDUTOR_MOTIVO_OPTIONS;
  readonly desvincularOutroMinLength = B2B_DESVINCULAR_OUTRO_MIN_LENGTH;

  substituirForm: FormGroup;
  devolverForm: FormGroup;

  /** Filtro local da tabela nativa de veículos (substitui busca do app-table). */
  vehicleSearchTerm = '';

  private pendingSnapshotId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService,
    private vehiclesService: VehiclesService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.substituirForm = this.fb.group({
      vehicleId: [null as number | null],
      plate: ['', [Validators.required]],
      model: ['', [Validators.required]],
      year: [new Date().getFullYear(), [Validators.required, Validators.min(1900)]],
      color: [''],
      brand: [''],
      renavam: [''],
      chassis: [''],
      currentKm: [0, [Validators.required, Validators.min(0)]],
      inspectionNotes: [''],
      observacao: ['']
    });

    this.devolverForm = this.fb.group({
      motivo: ['', [Validators.required, Validators.minLength(3)]]
    });

    this.desvincularForm = this.fb.group({
      motivo: ['', [Validators.required]],
      returnDate: [null as string | null]
    });

    this.desvincularOutroForm = this.fb.group({
      detalhe: [
        '',
        [Validators.required, Validators.minLength(B2B_DESVINCULAR_OUTRO_MIN_LENGTH)]
      ]
    });
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.contractId = +params['id'];
      this.itemId = +params['itemId'];
      if (this.contractId && this.itemId) {
        this.loadItem();
        this.loadContractInfo();
      }
    });
    this.route.queryParamMap.subscribe((q) => {
      const raw = q.get('snapshotId');
      const n = raw ? Number(raw) : NaN;
      this.pendingSnapshotId = Number.isFinite(n) ? n : null;
      this.tryOpenPendingSnapshot();
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;

    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (contract) => {
        this.contractNumber = contract.contractNumber || '';
        this.contractName = contract.contractName || '';
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
      }
    });
  }

  loadItem(): void {
    if (!this.contractId || !this.itemId) return;

    this.isLoading = true;

    this.contractsService.getB2bContractItemById(this.contractId, this.itemId).subscribe({
      next: (item) => {
        this.item = item;
        this.isLoading = false;
        this.tryOpenPendingSnapshot();
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoading = false;
      }
    });
  }

  /** Linhas exibidas na tabela HTML (com filtro de pesquisa). */
  get filteredVehicleRows(): any[] {
    const rows = this.getVehicleSnapshots();
    const q = (this.vehicleSearchTerm || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((v) => {
      const hay = [v.plate, v.model, v.brand, v.renavam, v.chassis, v.color, v.trackerType, v.trackerId]
        .map((x) => String(x ?? '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
  }

  clickTrocarVeiculo(v: any): void {
    this.onVehicleAction({ action: 'trocar', item: v });
  }

  clickVisualizarVeiculo(v: any): void {
    if (!this.contractId || !this.itemId || !v?.id) {
      this.errorService.showErrorMessage('Não foi possível identificar o veículo para visualizar.');
      return;
    }
    this.router.navigate([
      '/b2b/contracts',
      this.contractId,
      'items',
      this.itemId,
      'vehicles',
      v.id,
      'view'
    ]);
  }

  private tryOpenPendingSnapshot(): void {
    if (!this.pendingSnapshotId || !this.item || !this.contractId || !this.itemId) return;
    const snapshotId = this.pendingSnapshotId;
    this.pendingSnapshotId = null;
    this.router.navigate(
      ['/b2b/contracts', this.contractId, 'items', this.itemId, 'vehicles', snapshotId, 'view'],
      { replaceUrl: true }
    );
  }

  get pedidoNumber(): string {
    return this.item?.itemNumber || this.item?.ItemNumber || 'N/A';
  }

  formatTracker(v: any): string {
    const tipo = (v?.trackerType || '').toString().trim();
    const id = (v?.trackerId || '').toString().trim();
    if (tipo && id) return `${tipo} · ${id}`;
    if (tipo) return tipo;
    if (id) return id;
    return '—';
  }

  clickDevolverVeiculo(v: any): void {
    this.onVehicleAction({ action: 'devolver', item: v });
  }

  /** Desvincular condutor da moto neste linha (mesmo item do pedido — não é outro pedido). */
  clickDesvincularCondutor(v: any): void {
    if (!v?.id) {
      this.errorService.showErrorMessage('Não foi possível identificar o veículo (snapshot) neste item.');
      return;
    }
    if (!v.hasDriver) {
      this.errorService.showErrorMessage('Não há condutor vinculado a esta moto.');
      return;
    }
    this.vehicleRowForDesvincular = v;
    this.desvincularForm.reset({ motivo: '', returnDate: null });
    this.desvincularOutroForm.reset({ detalhe: '' });
    this.showDesvincularOutroModal = false;
    this.showDesvincularModal = true;
    this.cdr.detectChanges();
  }

  closeDesvincularModal(): void {
    if (this.isSavingDesvincular) return;
    this.showDesvincularModal = false;
    this.showDesvincularOutroModal = false;
    this.vehicleRowForDesvincular = null;
    this.desvincularForm.reset({ motivo: '', returnDate: null });
    this.desvincularOutroForm.reset({ detalhe: '' });
  }

  closeDesvincularOutroModal(): void {
    if (this.isSavingDesvincular) return;
    this.showDesvincularOutroModal = false;
    this.desvincularOutroForm.reset({ detalhe: '' });
    this.showDesvincularModal = true;
    this.cdr.detectChanges();
  }

  submitDesvincular(): void {
    if (!this.contractId || !this.itemId || !this.vehicleRowForDesvincular?.id) return;
    this.desvincularForm.markAllAsTouched();
    const motivoCode = (this.desvincularForm.get('motivo')?.value || '').toString().trim();
    if (!motivoCode || this.desvincularForm.invalid) {
      this.errorService.showErrorMessage('Selecione o motivo da desvinculação.');
      return;
    }

    if (motivoCode === B2bDesvincularCondutorMotivo.Outro) {
      this.showDesvincularModal = false;
      this.desvincularOutroForm.reset({ detalhe: '' });
      this.showDesvincularOutroModal = true;
      this.cdr.detectChanges();
      return;
    }

    this.executeDesvincular(motivoCode);
  }

  submitDesvincularOutro(): void {
    this.desvincularOutroForm.markAllAsTouched();
    const detalhe = (this.desvincularOutroForm.get('detalhe')?.value || '').toString().trim();
    const resolved = resolveDesvincularMotivoPayload(B2bDesvincularCondutorMotivo.Outro, detalhe);
    if (!resolved.ok) {
      this.errorService.showErrorMessage(resolved.error);
      return;
    }
    this.executeDesvincular(resolved.motivo);
  }

  private executeDesvincular(motivo: string): void {
    if (!this.contractId || !this.itemId || !this.vehicleRowForDesvincular?.id) return;

    const payload: { motivo: string; returnDate?: string } = { motivo };
    const rd = this.desvincularForm.get('returnDate')?.value;
    if (rd != null && String(rd).trim() !== '') {
      payload.returnDate = formatB2bDevolucaoReturnDateUtc(String(rd));
    }
    const driverKey = Number(
      this.vehicleRowForDesvincular.b2BContractDriverId ||
        this.vehicleRowForDesvincular.b2BContractItemDriverId ||
        this.vehicleRowForDesvincular.id
    );
    this.isSavingDesvincular = true;
    this.contractsService
      .unassignDriverFromVehicle(this.contractId, this.itemId, driverKey, payload)
      .pipe(finalize(() => (this.isSavingDesvincular = false)))
      .subscribe({
        next: () => {
          this.errorService.showSuccessMessage('Condutor desvinculado desta moto com sucesso.');
          this.closeDesvincularModal();
          this.loadItem();
        },
        error: () => {}
      });
  }

  onVehicleAction(evt: { action: string; item: any }): void {
    if (!evt?.item) {
      this.errorService.showErrorMessage('Não foi possível identificar a linha do veículo.');
      return;
    }
    if (evt.action === 'trocar') {
      if (evt.item.id == null) {
        this.errorService.showErrorMessage(
          'Não foi possível identificar o snapshot do veículo para troca. Verifique se a API retorna o ID do veículo no pedido.'
        );
        return;
      }
      this.selectedVehicleRow = evt.item;
      this.openSubstituirModal();
      return;
    }
    if (evt.action === 'devolver') {
      this.openDevolverModal(evt.item);
    }
  }

  openSubstituirModal(): void {
    if (!this.selectedVehicleRow) return;
    this.substituirForm.reset({
      vehicleId: null,
      plate: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      brand: '',
      renavam: '',
      chassis: '',
      currentKm: 0,
      inspectionNotes: '',
      observacao: ''
    });
    this.showSubstituirModal = true;
    this.cdr.detectChanges();
  }

  closeSubstituirModal(): void {
    this.showSubstituirModal = false;
    this.selectedVehicleRow = null;
  }

  /**
   * Devolução do veículo: somente `POST .../items/{itemId}/devolucao` — não confundir com troca/substituição.
   * @param placaVeiculo quando vem da linha da tabela, pré-preenche a placa de referência.
   */
  openDevolverModal(vehicleRow?: any): void {
    this.devolverVehicleSnapshotId = vehicleRow?.id ?? null;
    this.devolverForm.reset({
      motivo: ''
    });
    this.showDevolverModal = true;
    this.cdr.detectChanges();
  }

  closeDevolverModal(): void {
    this.showDevolverModal = false;
    this.devolverVehicleSnapshotId = null;
  }

  searchVehicleByPlate(): void {
    const rawPlate = (this.substituirForm.get('plate')?.value || '').toString().trim();
    if (!rawPlate) {
      this.errorService.showErrorMessage('Informe a placa do veículo substituto.');
      return;
    }
    const plate = rawPlate.replace(/\s+/g, '').toUpperCase();
    this.isSearchingVehicle = true;
    this.vehiclesService.getByPlate(plate).subscribe({
      next: (vehicle) => {
        this.isSearchingVehicle = false;
        if (!vehicle) {
          this.errorService.showErrorMessage(`Nenhum veículo encontrado com a placa ${plate}.`);
          return;
        }
        this.substituirForm.patchValue({
          vehicleId: vehicle.id,
          plate: vehicle.plateNumber || plate,
          model: vehicle.model || '',
          year: vehicle.modelYear || vehicle.manufactureYear || new Date().getFullYear(),
          color: vehicle.color || '',
          brand: vehicle.brand || '',
          renavam: vehicle.renavam || vehicle.renavamNumber || '',
          chassis: vehicle.chassisNumber || '',
          currentKm: vehicle.currentMileage ?? 0
        });
        this.errorService.showSuccessMessage('Dados do veículo carregados pela placa.');
      },
      error: (err) => {
        this.isSearchingVehicle = false;
        this.errorService.handleHttpError(err);
      }
    });
  }

  submitSubstituir(): void {
    if (!this.contractId || !this.itemId || !this.selectedVehicleRow?.id) return;
    if (this.substituirForm.invalid) {
      this.substituirForm.markAllAsTouched();
      this.errorService.showErrorMessage('Preencha os dados do veículo substituto.');
      return;
    }
    const f = this.substituirForm.value;
    this.isSavingSubstituir = true;
    this.contractsService
      .substituirB2bItemVeiculo(this.contractId, this.itemId, this.selectedVehicleRow.id, {
        vehicleSnapshot: {
          vehicleId: f.vehicleId ?? null,
          plate: f.plate,
          model: f.model,
          year: Number(f.year),
          color: f.color || '',
          brand: f.brand || '',
          renavam: f.renavam || '',
          chassis: f.chassis || '',
          currentKm: Number(f.currentKm) || 0,
          inspectionNotes: f.inspectionNotes || null,
          trackerType: null,
          trackerId: null,
          hasBox: false
        },
        observacao: f.observacao || null
      })
      .pipe(finalize(() => (this.isSavingSubstituir = false)))
      .subscribe({
        next: () => {
          this.errorService.showSuccessMessage('Veículo substituído com sucesso.');
          this.closeSubstituirModal();
          this.loadItem();
        },
        error: () => {}
      });
  }

  submitDevolver(): void {
    if (!this.contractId || !this.itemId || !this.item) return;
    if (this.devolverForm.invalid) {
      this.devolverForm.markAllAsTouched();
      this.errorService.showErrorMessage('Preencha o motivo da devolução.');
      return;
    }

    const activeSnapshotsBefore = this.getVehicleSnapshots().length;
    const returningLastVehicle =
      !!this.devolverVehicleSnapshotId && activeSnapshotsBefore <= 1;
    const devolverPedidoInteiro = !this.devolverVehicleSnapshotId;

    if (returningLastVehicle || devolverPedidoInteiro) {
      const pedidoLabel = this.pedidoNumber !== 'N/A' ? this.pedidoNumber : String(this.itemId);
      this.confirmationService.confirm({
        title: 'Excluir pedido?',
        message: devolverPedidoInteiro
          ? `Ao confirmar a devolução, o pedido "${pedidoLabel}" será excluído. Deseja continuar?`
          : `Este é o último veículo do pedido "${pedidoLabel}". Ao devolvê-lo, o pedido será excluído. Deseja apagar este pedido?`,
        confirmText: 'Sim, apagar pedido',
        cancelText: 'Cancelar',
        confirmButtonClass: 'btn-danger',
        cancelButtonClass: 'btn-secondary',
        icon: 'fas fa-exclamation-triangle',
        type: 'danger'
      }).subscribe({
        next: (ok) => {
          if (!ok) return;
          this.executeDevolver(true);
        }
      });
      return;
    }

    this.executeDevolver(false);
  }

  private executeDevolver(pedidoSeraRemovido: boolean): void {
    if (!this.contractId || !this.itemId) return;

    const motivo = (this.devolverForm.get('motivo')?.value || '').toString().trim();
    this.isSavingDevolver = true;
    const request$ = this.devolverVehicleSnapshotId
      ? this.contractsService.registrarDevolucaoB2bItemVeiculo(
          this.contractId,
          this.itemId,
          this.devolverVehicleSnapshotId,
          { motivo }
        )
      : this.contractsService.registrarDevolucaoB2bItem(this.contractId, this.itemId, { motivo });

    request$
      .pipe(finalize(() => (this.isSavingDevolver = false)))
      .subscribe({
        next: (res) => {
          const kind = (res?.kind ?? res?.Kind ?? '').toString();
          const itemAfter = res?.item ?? res?.Item;
          const pedidoRemovido =
            pedidoSeraRemovido
            || (kind === 'vehicle_snapshot' && itemAfter == null);

          this.errorService.showSuccessMessage(
            pedidoRemovido
              ? 'Veículo devolvido e pedido excluído com sucesso.'
              : 'Devolução registrada com sucesso.'
          );
          this.closeDevolverModal();

          if (pedidoRemovido) {
            this.navigateBack();
            return;
          }

          this.loadItem();
        },
        error: () => {}
      });
  }

  navigateToEdit(): void {
    if (!this.contractId || !this.itemId) return;
    this.router.navigate(['/b2b/contracts', this.contractId, 'items', this.itemId, 'edit']);
  }

  navigateBack(): void {
    if (this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'items']);
    } else {
      this.router.navigate(['/b2b/contracts']);
    }
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  }

  formatCurrency(value: number): string {
    if (!value && value !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      Active: 'Ativo',
      Inactive: 'Inativo',
      Pending: 'Pendente'
    };
    return statusMap[status] || status || 'N/A';
  }

  getStatusClass(status: string): string {
    const statusUpper = (status || '').toUpperCase();
    if (statusUpper.includes('ACTIVE') || statusUpper.includes('ATIVO')) {
      return 'badge-success';
    }
    if (statusUpper.includes('INACTIVE') || statusUpper.includes('INATIVO')) {
      return 'badge-secondary';
    }
    return 'badge-info';
  }

  getVehicleSnapshots(): any[] {
    if (!this.item) return [];
    const vehicles = this.item.vehicleSnapshots || this.item.VehicleSnapshots || [];
    return vehicles.map((v: any) => {
      const rawId =
        v.id ??
        v.Id ??
        v.b2BContractItemVehicleSnapshotId ??
        v.B2BContractItemVehicleSnapshotId ??
        v.vehicleSnapshotId ??
        v.VehicleSnapshotId;
      let snapshotId: number | null = null;
      if (rawId !== null && rawId !== undefined && rawId !== '') {
        const n = Number(rawId);
        snapshotId = Number.isFinite(n) ? n : null;
      }
      const assoc = this.findActiveDriverAssociation(snapshotId);
      const snapshotHasDriver = !!(v.hasDriver ?? v.HasDriver ?? false);
      const hasDriver =
        snapshotHasDriver ||
        !!assoc ||
        !!(v.b2BContractDriverId ?? v.B2BContractDriverId);
      const snapshotDriverName = (v.driverName || v.DriverName || '').toString().trim();
      return {
        plate: v.plate || v.Plate || 'N/A',
        model: v.model || v.Model || 'N/A',
        year: v.year || v.Year || 'N/A',
        color: v.color || v.Color || 'N/A',
        brand: v.brand || v.Brand || 'N/A',
        renavam: v.renavam || v.Renavam || 'N/A',
        chassis: v.chassis || v.Chassis || 'N/A',
        currentKm: v.currentKm ?? v.CurrentKm ?? 0,
        inspectionNotes: v.inspectionNotes || v.InspectionNotes || '',
        trackerType: v.trackerType || v.TrackerType || null,
        trackerId: v.trackerId || v.TrackerId || null,
        hasBox: v.hasBox || v.HasBox || false,
        hasPhoneSupport: !!(v.hasPhoneSupport ?? v.HasPhoneSupport),
        operationalStatus: v.operationalStatus || v.OperationalStatus || null,
        hasDriver,
        driverDisplay: this.getDriverDisplayForSnapshot(snapshotId, hasDriver, snapshotDriverName),
        b2BContractDriverId:
          v.b2BContractDriverId ??
          v.B2BContractDriverId ??
          assoc?.b2BContractDriverId ??
          assoc?.B2BContractDriverId ??
          null,
        b2BContractItemDriverId:
          v.b2BContractItemDriverId ?? v.B2BContractItemDriverId ?? assoc?.id ?? assoc?.Id ?? null,
        id: snapshotId
      };
    });
  }

  /** Associação vigente do snapshot (ignora Inactive/Replaced e com EndDate). */
  private findActiveDriverAssociation(snapshotId: number | null): any | null {
    if (snapshotId == null || !this.item) return null;
    const assocs = this.item.driverAssociations || this.item.DriverAssociations || [];
    return (
      assocs.find((x: any) => {
        const sid = x.b2BContractItemVehicleSnapshotId ?? x.B2BContractItemVehicleSnapshotId;
        if (sid !== snapshotId) return false;
        return this.isDriverAssociationActive(x);
      }) || null
    );
  }

  private isDriverAssociationActive(a: any): boolean {
    if (!a) return false;
    if (a.isDeleted || a.IsDeleted) return false;
    const status = String(a.status ?? a.Status ?? '')
      .trim()
      .toLowerCase();
    if (['inactive', 'inativo', 'replaced', 'substituido', 'substituído'].includes(status)) {
      return false;
    }
    const end = a.endDate ?? a.EndDate;
    if (end != null && String(end).trim() !== '') return false;
    return true;
  }

  /** Nome do condutor: snapshot.driverName ou associação ativa. */
  private getDriverDisplayForSnapshot(
    snapshotId: number | null,
    hasDriver: boolean,
    snapshotDriverName = ''
  ): string {
    if (!hasDriver) return '—';
    if (snapshotDriverName) return snapshotDriverName;
    if (!snapshotId || !this.item) return 'Atribuído';
    const a = this.findActiveDriverAssociation(snapshotId);
    if (!a) return 'Atribuído';
    const name =
      a.driverName ||
      a.DriverName ||
      a.condutorNome ||
      (a.driver && (a.driver.name || a.driver.Name)) ||
      (a.Driver && (a.Driver.Name || a.Driver.name));
    return name ? String(name) : 'Atribuído';
  }

  formatKm(value: number): string {
    if (!value && value !== 0) return '0 km';
    return `${Number(value).toLocaleString('pt-BR')} km`;
  }

  get placaAtualSubstituicao(): string {
    return this.selectedVehicleRow?.plate || '—';
  }

}
